import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Lock, Play, X, Languages, Users, MessageSquare, Send } from "lucide-react";
import { toast } from "sonner";
import { api, API_BASE } from "../lib/api";
import { useAuth } from "../lib/auth";

const ORIGINAL_AUDIO = "__original__";

export default function Watch() {
  const { id } = useParams();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const epId = sp.get("ep");
  const partyId = sp.get("party");
  const { user, profile } = useAuth();
  const [v, setV] = useState(null);
  const [episode, setEpisode] = useState(null);
  const [resumeAt, setResumeAt] = useState(0);
  const [nextEp, setNextEp] = useState(null);
  const [countdown, setCountdown] = useState(null); // null | 10..0
  const [playbackError, setPlaybackError] = useState(null);
  const [audioLang, setAudioLang] = useState(ORIGINAL_AUDIO);
  const [audioMenuOpen, setAudioMenuOpen] = useState(false);
  const [party, setParty] = useState(null); // { is_host, viewers, names }
  const [chatMessages, setChatMessages] = useState([]); // [{from,text,ts}]
  const [chatOpen, setChatOpen] = useState(false);
  const [chatDraft, setChatDraft] = useState("");
  const [chatUnread, setChatUnread] = useState(0);
  const [floatingEmojis, setFloatingEmojis] = useState([]); // [{id, emoji, x, from}]
  const videoRef = useRef(null);
  const audioRef = useRef(null);
  const lastSentRef = useRef(0);
  const timerRef = useRef(null);
  const wsRef = useRef(null);
  const partyTickRef = useRef(0);
  const applyingRemoteRef = useRef(false);
  const chatEndRef = useRef(null);
  const chatOpenRef = useRef(false);

  useEffect(() => { chatOpenRef.current = chatOpen; }, [chatOpen]);

  useEffect(() => {
    api.get(`/videos/${id}`).then((r) => setV(r.data)).catch(() => toast.error("Video not found"));
    setNextEp(null); setCountdown(null); setPlaybackError(null);
    if (epId) {
      api.get(`/episodes/${epId}`).then((r) => setEpisode(r.data)).catch(() => {});
      api.get(`/episodes/${epId}/next`).then((r) => {
        if (r.data && r.data.episode_id) setNextEp(r.data);
      }).catch(() => {});
    } else {
      setEpisode(null);
    }
    if (profile) {
      api.get(`/playback/${id}`, { params: { profile_id: profile.profile_id, episode_id: epId || undefined } })
        .then((r) => setResumeAt(r.data?.position_seconds || 0))
        .catch(() => {});
    }
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [id, epId, profile]);

  const onLoaded = () => {
    if (videoRef.current && resumeAt > 5) {
      videoRef.current.currentTime = resumeAt;
    }
  };

  // ---- Sync hidden <audio> with main <video> when alt audio is active ----
  useEffect(() => {
    const vid = videoRef.current;
    const aud = audioRef.current;
    if (!vid || !aud) return;
    if (audioLang === ORIGINAL_AUDIO) return;

    const sync = () => {
      const drift = Math.abs(aud.currentTime - vid.currentTime);
      if (drift > 0.3) aud.currentTime = vid.currentTime;
    };
    const onPlay = () => { aud.currentTime = vid.currentTime; aud.play().catch(() => {}); };
    const onPause = () => aud.pause();
    const onSeek = () => { aud.currentTime = vid.currentTime; };
    const onRate = () => { aud.playbackRate = vid.playbackRate; };

    vid.addEventListener("play", onPlay);
    vid.addEventListener("pause", onPause);
    vid.addEventListener("seeking", onSeek);
    vid.addEventListener("seeked", onSeek);
    vid.addEventListener("ratechange", onRate);
    vid.addEventListener("timeupdate", sync);
    aud.playbackRate = vid.playbackRate;
    if (!vid.paused) onPlay();

    return () => {
      vid.removeEventListener("play", onPlay);
      vid.removeEventListener("pause", onPause);
      vid.removeEventListener("seeking", onSeek);
      vid.removeEventListener("seeked", onSeek);
      vid.removeEventListener("ratechange", onRate);
      vid.removeEventListener("timeupdate", sync);
      aud.pause();
    };
  }, [audioLang, v?.id, episode?.episode_id]);

  // ---- Watch Party WebSocket ----
  useEffect(() => {
    if (!partyId || !v) return;
    let cancelled = false;
    let ws = null;
    (async () => {
      try {
        const { data: room } = await api.get(`/watchparty/${partyId}`);
        if (cancelled) return;
        setParty({ is_host: room.is_host, viewers: room.viewers, names: [], host_name: room.host_name });
        const token = localStorage.getItem("cc_token");
        const wsUrl = `${API_BASE.replace(/^http/, "ws")}/watchparty/ws/${partyId}?token=${encodeURIComponent(token || "")}`;
        ws = new WebSocket(wsUrl);
        wsRef.current = ws;
        ws.onmessage = (ev) => {
          const m = JSON.parse(ev.data);
          if (m.type === "presence") {
            setParty((p) => p ? { ...p, viewers: m.viewers, names: m.names || [] } : p);
          } else if (m.type === "chat") {
            setChatMessages((prev) => [...prev, m].slice(-200));
            if (!chatOpenRef.current) setChatUnread((u) => u + 1);
          } else if (m.type === "reaction") {
            const id = `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
            const x = 20 + Math.random() * 60; // 20-80 % from left
            setFloatingEmojis((prev) => [...prev, { id, emoji: m.emoji, x, from: m.from }]);
            setTimeout(() => setFloatingEmojis((prev) => prev.filter((e) => e.id !== id)), 2200);
          } else if (m.type === "play" || m.type === "pause" || m.type === "seek" || m.type === "tick") {
            const vid = videoRef.current;
            if (!vid) return;
            const drift = Math.abs((vid.currentTime || 0) - (m.position || 0));
            applyingRemoteRef.current = true;
            try {
              if (drift > 1.5) vid.currentTime = m.position || 0;
              if (m.type === "play" && vid.paused) vid.play().catch(() => {});
              if (m.type === "pause" && !vid.paused) vid.pause();
            } finally {
              setTimeout(() => { applyingRemoteRef.current = false; }, 80);
            }
          }
        };
        ws.onclose = () => { wsRef.current = null; };
        ws.onerror = () => { /* swallow */ };
      } catch (e) {
        toast.error("Watch Party not found or expired");
      }
    })();
    return () => {
      cancelled = true;
      if (ws) try { ws.close(); } catch {}
      wsRef.current = null;
    };
  }, [partyId, v?.id]);

  // Host broadcasters: play/pause/seek/tick
  useEffect(() => {
    if (!party?.is_host || !partyId) return;
    const vid = videoRef.current;
    if (!vid) return;
    const send = (type) => {
      const ws = wsRef.current;
      if (ws && ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ type, position: vid.currentTime || 0 }));
      }
    };
    const onPlay = () => { if (!applyingRemoteRef.current) send("play"); };
    const onPause = () => { if (!applyingRemoteRef.current) send("pause"); };
    const onSeek = () => { if (!applyingRemoteRef.current) send("seek"); };
    const onTick = () => {
      const now = Date.now();
      if (now - partyTickRef.current > 2000) { partyTickRef.current = now; send("tick"); }
    };
    vid.addEventListener("play", onPlay);
    vid.addEventListener("pause", onPause);
    vid.addEventListener("seeked", onSeek);
    vid.addEventListener("timeupdate", onTick);
    return () => {
      vid.removeEventListener("play", onPlay);
      vid.removeEventListener("pause", onPause);
      vid.removeEventListener("seeked", onSeek);
      vid.removeEventListener("timeupdate", onTick);
    };
  }, [party?.is_host, partyId, v?.id]);

  // Chat auto-scroll + unread reset on open
  useEffect(() => {
    if (chatOpen) setChatUnread(0);
    if (chatOpen && chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [chatOpen, chatMessages.length]);

  const sendChat = (e) => {
    e?.preventDefault();
    const text = chatDraft.trim();
    if (!text) return;
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) {
      toast.error("Not connected to party");
      return;
    }
    ws.send(JSON.stringify({ type: "chat", text }));
    setChatDraft("");
  };

  const sendReaction = (emoji) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    ws.send(JSON.stringify({ type: "reaction", emoji }));
  };

  const onTimeUpdate = () => {
    if (!videoRef.current || !profile) return;
    const t = videoRef.current.currentTime;
    const d = videoRef.current.duration || 0;
    if (Math.abs(t - lastSentRef.current) >= 10 && d > 0) {
      lastSentRef.current = t;
      api.post(`/playback/${id}`, {
        profile_id: profile.profile_id,
        position_seconds: t,
        duration_seconds: d,
        episode_id: epId || null,
      }).catch(() => {});
    }
  };

  const onEnded = () => {
    if (profile && videoRef.current) {
      api.post(`/playback/${id}`, {
        profile_id: profile.profile_id,
        position_seconds: videoRef.current.duration,
        duration_seconds: videoRef.current.duration,
        episode_id: epId || null,
      }).catch(() => {});
    }
    if (nextEp) startCountdown();
  };

  const startCountdown = () => {
    setCountdown(10);
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setCountdown((c) => {
        if (c === null) return null;
        if (c <= 1) {
          clearInterval(timerRef.current);
          playNext();
          return null;
        }
        return c - 1;
      });
    }, 1000);
  };

  const cancelCountdown = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    setCountdown(null);
  };

  const playNext = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    setCountdown(null);
    if (nextEp?.episode_id) nav(`/watch/${id}?ep=${nextEp.episode_id}`);
  };

  const hasSub = user?.subscription_status === "active";

  if (!v) return <div className="min-h-screen bg-black" />;

  if (!hasSub) {
    return (
      <div className="min-h-screen bg-black text-white flex flex-col items-center justify-center p-6 text-center">
        <Lock className="w-16 h-16 text-[#E50914] mb-6" />
        <h1 className="text-3xl md:text-4xl font-bold mb-2">Subscription required</h1>
        <p className="text-gray-400 max-w-md mb-6">Pick a plan to watch unlimited movies and shows in glorious HD & 4K.</p>
        <div className="flex gap-3">
          <button data-testid="watch-pricing-btn" onClick={() => nav("/pricing")} className="btn-red px-8 py-3 rounded font-semibold">View Plans</button>
          <button onClick={() => nav(-1)} className="btn-glass px-8 py-3 rounded font-semibold">Back</button>
        </div>
      </div>
    );
  }

  // If stream_url is a backend storage path, append ?auth=token (browsers can't send headers in <video src>)
  const token = typeof window !== "undefined" ? localStorage.getItem("cc_token") : null;
  const rawSrc = episode?.stream_url || v.stream_url;
  let src = rawSrc;
  if (src?.startsWith("/api/files/")) {
    const origin = API_BASE.replace(/\/api$/, "");
    src = `${origin}${rawSrc}${token ? `?auth=${encodeURIComponent(token)}` : ""}`;
  }

  // Build subtitle URLs the same way (use show-level subtitles)
  const subtitles = (v.subtitles || []).map((s) => {
    let url = s.url;
    if (url?.startsWith("/api/files/")) {
      const origin = API_BASE.replace(/\/api$/, "");
      url = `${origin}${s.url}`;
    }
    return { ...s, url };
  });

  // Build alternate audio-track URLs (browsers can't send headers in <audio src>)
  const audioTracks = (v.audio_tracks || []).map((a) => {
    let url = a.url;
    if (url?.startsWith("/api/files/")) {
      const origin = API_BASE.replace(/\/api$/, "");
      url = `${origin}${a.url}${token ? `?auth=${encodeURIComponent(token)}` : ""}`;
    }
    return { ...a, url };
  });
  const activeAudio = audioTracks.find((a) => a.lang === audioLang) || null;

  const displayTitle = episode ? `${v.title} · S${episode.season}E${episode.episode_number} ${episode.title}` : v.title;
  const posterImg = episode?.thumbnail || v.backdrop;
  // crossOrigin is only required for <track> subtitles; forcing it on external URLs without CORS breaks playback
  const needsCors = subtitles.length > 0;

  return (
    <div className="min-h-screen bg-black text-white relative">
      <button onClick={() => nav(-1)} className="absolute top-6 left-6 z-20 btn-glass px-4 py-2 rounded flex items-center gap-2">
        <ArrowLeft className="w-5 h-5" /> Back
      </button>
      {party && (
        <div data-testid="watchparty-badge" className="absolute top-6 left-32 z-20 bg-cyan-500/15 border border-cyan-400/30 backdrop-blur px-3 py-1.5 rounded flex items-center gap-2 text-xs">
          <Users className="w-4 h-4 text-cyan-300" />
          <span className="text-cyan-100 font-semibold">
            {party.is_host ? "Hosting" : `Synced with ${party.host_name || "host"}`}
          </span>
          <span className="text-cyan-300/80">· {party.viewers || 1} watching</span>
          <button
            data-testid="watchparty-copy-btn"
            onClick={async () => {
              try {
                await navigator.clipboard.writeText(window.location.href);
                toast.success("Watch Party link copied!");
              } catch { toast.error("Could not copy link"); }
            }}
            className="ml-1 text-cyan-200 hover:text-white underline"
          >Copy link</button>
          <button
            data-testid="watchparty-chat-toggle"
            onClick={() => setChatOpen((o) => !o)}
            className="relative ml-1 text-cyan-200 hover:text-white flex items-center gap-1"
            aria-label="toggle chat"
          >
            <MessageSquare className="w-3.5 h-3.5" /> Chat
            {chatUnread > 0 && (
              <span data-testid="watchparty-chat-unread" className="absolute -top-2 -right-3 bg-[#E50914] text-white text-[10px] font-bold px-1.5 rounded-full min-w-[16px] text-center">
                {chatUnread > 9 ? "9+" : chatUnread}
              </span>
            )}
          </button>
        </div>
      )}
      <div className="absolute top-6 right-6 z-20 text-sm bg-black/60 backdrop-blur px-3 py-1 rounded">
        {displayTitle}{resumeAt > 5 && <span className="ml-2 text-[#E50914] text-xs">Resuming…</span>}
      </div>
      <div className="w-full h-screen flex items-center justify-center bg-black">
        {!src ? (
          <div data-testid="no-source" className="text-center px-6">
            <Lock className="w-12 h-12 text-[#E50914] mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-2">No stream available</h2>
            <p className="text-gray-400 mb-4">This title does not have a playable video source yet.</p>
            <button onClick={() => nav(-1)} className="btn-glass px-6 py-2 rounded font-semibold">Back</button>
          </div>
        ) : playbackError ? (
          <div data-testid="playback-error" className="text-center px-6 max-w-md">
            <Lock className="w-12 h-12 text-[#E50914] mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-2">Playback error</h2>
            <p className="text-gray-400 mb-4">We couldn't load this video. It may be temporarily unavailable.</p>
            <div className="flex gap-3 justify-center">
              <button data-testid="playback-retry-btn" onClick={() => { setPlaybackError(null); if (videoRef.current) { videoRef.current.load(); } }} className="btn-red px-6 py-2 rounded font-semibold">Retry</button>
              <button onClick={() => nav(-1)} className="btn-glass px-6 py-2 rounded font-semibold">Back</button>
            </div>
          </div>
        ) : (
        <video
          ref={videoRef}
          data-testid="video-player"
          src={src}
          controls
          autoPlay
          muted={audioLang !== ORIGINAL_AUDIO}
          {...(needsCors ? { crossOrigin: "anonymous" } : {})}
          poster={posterImg}
          onLoadedMetadata={onLoaded}
          onTimeUpdate={onTimeUpdate}
          onEnded={onEnded}
          onError={() => setPlaybackError(true)}
          className="w-full h-full object-contain"
        >
          {subtitles.map((s, i) => (
            <track
              key={s.lang || i}
              data-testid={`track-${s.lang || i}`}
              kind="subtitles"
              src={s.url}
              srcLang={s.lang}
              label={s.label || s.lang}
              default={i === 0}
            />
          ))}
          Your browser does not support HTML5 video.
        </video>
        )}
        {/* Hidden parallel <audio> element used for alternate-language tracks */}
        {activeAudio && (
          <audio
            ref={audioRef}
            data-testid="alt-audio"
            src={activeAudio.url}
            preload="auto"
            className="hidden"
          />
        )}
      </div>

      {/* Audio-track language picker (only when alt tracks exist) */}
      {audioTracks.length > 0 && !playbackError && src && (
        <div className="absolute top-6 right-48 z-30">
          <button
            data-testid="audio-lang-btn"
            onClick={() => setAudioMenuOpen((o) => !o)}
            className="btn-glass px-3 py-2 rounded flex items-center gap-2 text-sm"
            title="Audio language"
          >
            <Languages className="w-4 h-4" />
            <span className="hidden md:inline">
              {audioLang === ORIGINAL_AUDIO ? "Original" : (audioTracks.find((a) => a.lang === audioLang)?.label || audioLang)}
            </span>
          </button>
          {audioMenuOpen && (
            <div data-testid="audio-lang-menu" className="absolute right-0 mt-2 w-56 bg-[#0a0a0a]/95 backdrop-blur-xl border border-white/10 rounded-lg shadow-2xl py-1 overflow-hidden">
              <div className="px-3 py-2 text-[10px] uppercase tracking-widest text-gray-500 border-b border-white/5">Audio</div>
              <button
                data-testid="audio-opt-original"
                onClick={() => { setAudioLang(ORIGINAL_AUDIO); setAudioMenuOpen(false); }}
                className={`w-full text-left px-4 py-2 text-sm hover:bg-white/5 flex items-center justify-between ${audioLang === ORIGINAL_AUDIO ? "text-[#E50914]" : "text-white"}`}
              >
                Original
                {audioLang === ORIGINAL_AUDIO && <span>●</span>}
              </button>
              {audioTracks.map((a) => (
                <button
                  key={a.lang}
                  data-testid={`audio-opt-${a.lang}`}
                  onClick={() => { setAudioLang(a.lang); setAudioMenuOpen(false); }}
                  className={`w-full text-left px-4 py-2 text-sm hover:bg-white/5 flex items-center justify-between ${audioLang === a.lang ? "text-[#E50914]" : "text-white"}`}
                >
                  {a.label || a.lang}
                  {audioLang === a.lang && <span>●</span>}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Auto-play next episode overlay */}
      {countdown !== null && nextEp && (
        <div data-testid="autoplay-overlay" className="absolute bottom-8 right-8 z-30 w-[420px] bg-[#0a0a0a]/95 backdrop-blur-xl border border-white/10 rounded-lg p-5 shadow-[0_24px_60px_rgba(0,0,0,0.9)] animate-fade-up">
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="relative w-9 h-9">
                <svg className="w-9 h-9 -rotate-90" viewBox="0 0 36 36">
                  <circle cx="18" cy="18" r="15" fill="none" stroke="#ffffff20" strokeWidth="3" />
                  <circle cx="18" cy="18" r="15" fill="none" stroke="#E50914" strokeWidth="3"
                    strokeDasharray={`${(countdown / 10) * 94.25} 94.25`} strokeLinecap="round" />
                </svg>
                <span className="absolute inset-0 flex items-center justify-center text-xs font-bold">{countdown}</span>
              </div>
              <span className="text-xs uppercase tracking-widest text-gray-400">Up next in {countdown}s</span>
            </div>
            <button onClick={cancelCountdown} className="text-gray-400 hover:text-white" aria-label="cancel"><X className="w-4 h-4" /></button>
          </div>
          <div className="flex gap-3 mb-4">
            {nextEp.thumbnail ? (
              <img src={nextEp.thumbnail} alt="" className="w-28 h-16 object-cover rounded flex-shrink-0" />
            ) : (
              <div className="w-28 h-16 bg-[#1a1a1a] rounded flex items-center justify-center flex-shrink-0">
                <span className="text-xs text-gray-500">S{nextEp.season}E{nextEp.episode_number}</span>
              </div>
            )}
            <div className="flex-1 min-w-0">
              <div className="text-[11px] text-gray-500 uppercase tracking-wider">S{nextEp.season} · Episode {nextEp.episode_number}</div>
              <div className="text-sm font-semibold line-clamp-1">{nextEp.title}</div>
              <div className="text-xs text-gray-400 line-clamp-2 mt-0.5">{nextEp.description}</div>
            </div>
          </div>
          <div className="flex gap-2">
            <button data-testid="autoplay-now-btn" onClick={playNext} className="btn-red flex-1 py-2.5 rounded font-semibold text-sm flex items-center justify-center gap-2">
              <Play className="w-4 h-4 fill-current" /> Play Now
            </button>
            <button data-testid="autoplay-cancel-btn" onClick={cancelCountdown} className="btn-glass px-4 py-2.5 rounded font-semibold text-sm">Not now</button>
          </div>
        </div>
      )}

      {/* Watch Party emoji reaction dock */}
      {party && !playbackError && src && (
        <div data-testid="reaction-dock" className="absolute bottom-32 left-1/2 -translate-x-1/2 z-20 flex items-center gap-1 bg-black/70 backdrop-blur-lg border border-white/10 rounded-full px-2 py-1 shadow-2xl">
          {["🔥", "❤️", "😂", "👀", "🙌", "💀"].map((e) => (
            <button
              key={e}
              data-testid={`reaction-${e}`}
              onClick={() => {
                sendReaction(e);
                // Also show immediately for myself
                const id = `${Date.now()}-self-${Math.random().toString(36).slice(2, 5)}`;
                const x = 20 + Math.random() * 60;
                setFloatingEmojis((prev) => [...prev, { id, emoji: e, x, from: "you" }]);
                setTimeout(() => setFloatingEmojis((prev) => prev.filter((f) => f.id !== id)), 2200);
              }}
              className="text-xl hover:scale-125 active:scale-110 transition-transform p-1.5 rounded-full hover:bg-white/10"
              aria-label={`react ${e}`}
            >
              {e}
            </button>
          ))}
        </div>
      )}

      {/* Floating emoji animations */}
      {party && floatingEmojis.length > 0 && (
        <div className="absolute inset-0 z-30 pointer-events-none overflow-hidden">
          {floatingEmojis.map((f) => (
            <div
              key={f.id}
              className="absolute bottom-20 text-4xl animate-[float-up_2.2s_ease-out_forwards]"
              style={{ left: `${f.x}%` }}
            >
              {f.emoji}
            </div>
          ))}
        </div>
      )}

      {/* Watch Party chat drawer */}
      {party && chatOpen && (
        <div
          data-testid="watchparty-chat-panel"
          className="absolute top-0 right-0 bottom-0 w-full sm:w-[360px] z-40 bg-[#0a0a0a]/95 backdrop-blur-xl border-l border-cyan-500/20 flex flex-col shadow-[-24px_0_60px_rgba(0,0,0,0.8)]"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 flex-shrink-0">
            <div className="flex items-center gap-2">
              <MessageSquare className="w-4 h-4 text-cyan-300" />
              <span className="text-sm font-semibold">Party Chat</span>
              <span className="text-xs text-gray-400">· {party.viewers || 1} here</span>
            </div>
            <button data-testid="watchparty-chat-close" onClick={() => setChatOpen(false)} className="text-gray-400 hover:text-white" aria-label="close chat">
              <X className="w-4 h-4" />
            </button>
          </div>
          <div data-testid="watchparty-chat-messages" className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
            {chatMessages.length === 0 && (
              <div className="text-center text-xs text-gray-500 mt-6">
                Say hi — everyone in the party will see it.
              </div>
            )}
            {chatMessages.map((m, i) => (
              <div key={i} data-testid={`chat-msg-${i}`} className="text-sm">
                <div className="text-[11px] text-cyan-300 font-semibold">{m.from}</div>
                <div className="text-gray-100 break-words">{m.text}</div>
              </div>
            ))}
            <div ref={chatEndRef} />
          </div>
          <form onSubmit={sendChat} className="p-3 border-t border-white/10 flex-shrink-0 flex gap-2">
            <input
              data-testid="watchparty-chat-input"
              value={chatDraft}
              onChange={(e) => setChatDraft(e.target.value)}
              placeholder="Type a message…"
              maxLength={300}
              className="input-dark flex-1 text-sm"
            />
            <button
              data-testid="watchparty-chat-send"
              type="submit"
              disabled={!chatDraft.trim()}
              className="btn-red px-3 py-2 rounded text-sm disabled:opacity-40 disabled:cursor-not-allowed"
              aria-label="send"
            >
              <Send className="w-4 h-4" />
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
